import { useCallback } from "react";

import { TaskType } from "__graphql__/types";
import { PageName } from "analytics/events";
import { useCheckAlreadyCompletedToast } from "flows/Inventory/shared/hooks/useCheckAlreadyCompletedToast";
import { useChecksRedirect } from "flows/Inventory/shared/hooks/useChecksRedirect";
import { useStartNextCheck } from "flows/Inventory/shared/hooks/useStartNextCheck";
import { useTaskCompletedToast } from "flows/Inventory/shared/hooks/useTaskCompletedToast";
import { getPriorityGroup } from "flows/Inventory/shared/models/check/serializer";
import { useValidateStockCheckMutation } from "flows/Inventory/shared/queries/stockCheck/stockCheck.generated";
import { ErrorTypeForAnalytics, useAnalytics } from "shared/hooks/useAnalytics";
import { useErrorToastWithAudio } from "shared/hooks/useErrorToastWithAudio";
import { reportErrorsToDDAndThrow } from "utils/datadog";
import { isNotNullNorUndefined, isNullOrUndefined } from "utils/tsHelpers";

import { STOCK_CHECK_METADATA_REASONS } from "../constants";
import { StockCheckSessionStore } from "../stores/useStockCheckSessionStore";

export type StockCheckMutationContext = Pick<
  StockCheckSessionStore,
  | "checkId"
  | "origin"
  | "priority"
  | "shelfNumber"
  | "shelfLetter"
  | "sku"
  | "declaredQuantity"
  | "declareStockDetails"
>;

export type DeclareStockParams = {
  totalNewStock: number;
  quantityDamaged: number;
  quantityExpired: number;
  quantityTooGoodToGo: number;
  expectedQuantity: number;
};

export type UseStockCheckMutationResult = {
  declareStock: (params: DeclareStockParams) => Promise<void>;
  isUpdating: boolean;
};

export function useStockCheckMutation({
  context,
}: {
  context: StockCheckMutationContext;
}): UseStockCheckMutationResult {
  const [validateStockCheck, { loading: isUpdating }] = useValidateStockCheckMutation();
  const { showCheckAlreadyCompletedToast } = useCheckAlreadyCompletedToast();
  const { showToastAndPlayError } = useErrorToastWithAudio({
    errorToastId: "stock_check_error",
  });
  const showTaskCompletedToast = useTaskCompletedToast();
  const { navigateToCheck, navigateToOrigin } = useChecksRedirect();
  const { startNextCheck } = useStartNextCheck();
  const { sendSegmentTrackEvent } = useAnalytics();

  const declareStock = useCallback(
    async (params: DeclareStockParams) => {
      const {
        totalNewStock,
        quantityDamaged,
        quantityExpired,
        quantityTooGoodToGo,
        expectedQuantity,
      } = params;

      if (isNullOrUndefined(context.checkId)) {
        throw new Error("No checkId present in context");
      }

      try {
        await reportErrorsToDDAndThrow(
          "updating stock check",
          async () => {
            try {
              await validateStockCheck({
                variables: {
                  input: {
                    checkId: context.checkId!,
                    quantity: totalNewStock,
                    quantityDamaged,
                    quantityExpired,
                    quantityTooGoodToGo,
                  },
                },
              });
            } catch (e: any) {
              if (e?.graphQLErrors?.[0]?.extensions?.code === "TASK_UPDATE_CONFLICT") {
                throw new Error("taskAlreadyCompleted");
              }
              throw e;
            }
          },
          {
            context: {
              checkId: context.checkId,
              sku: context.sku,
              origin: context.origin,
              priority: context.priority,
              shelfNumber: context.shelfNumber,
              shelfLetter: context.shelfLetter,
              declaredQuantity: context.declaredQuantity,
              totalNewStock,
              quantityDamaged,
              quantityExpired,
              quantityTooGoodToGo,
            },
          },
        );

        // Track successful completion
        sendSegmentTrackEvent("stockCheckFinished", {
          type: "automated",
          check_id: context.checkId,
          product_sku: context.sku,
          quantity_expected: expectedQuantity,
          quantity_counted: context.declaredQuantity,
          quantity_damaged: quantityDamaged,
          quantity_expired: quantityExpired,
          quantity_tgtg: quantityTooGoodToGo,
        });

        // Store the details in the Zustand store
        context.declareStockDetails({
          expectedQuantity,
          remainingStock: totalNewStock,
          damagedQuantity: quantityDamaged,
          expiredQuantity: quantityExpired,
          tooGoodToGoQuantity: quantityTooGoodToGo,
        });

        // If origin is activities page, show toast and redirect directly
        if (context.origin === PageName.ACTIVITIES_PAGE) {
          showTaskCompletedToast();
          navigateToOrigin(context.origin);
          return;
        }

        // Try to fetch next check
        try {
          const result = await startNextCheck({
            groupedPriority: getPriorityGroup(context.priority),
            shelfNumber: `${context.shelfNumber}${context.shelfLetter}`,
            type: [TaskType.stock_check],
            metadataReason: STOCK_CHECK_METADATA_REASONS,
          });

          if (result.status === "noMoreCheck") {
            showTaskCompletedToast();
            navigateToOrigin(context.origin);
          } else if (result.status === "unknownError") {
            navigateToOrigin(context.origin);
          } else if (result.status === "checkAlreadyTaken") {
            navigateToOrigin(context.origin);
          } else if (isNotNullNorUndefined(result.check)) {
            navigateToCheck(result.check, context.origin);
          }
        } catch (error) {
          // If fetching next check fails, just go back to origin
          navigateToOrigin(context.origin);
        }
      } catch (e: any) {
        // Handle task already completed error
        if ((e?.message ?? e?.toString() ?? "").includes("taskAlreadyCompleted")) {
          showCheckAlreadyCompletedToast();
          navigateToOrigin(context.origin);
          return;
        }

        // Show generic error toast and track error
        showToastAndPlayError();
        sendSegmentTrackEvent("errorShown", {
          screen_name: PageName.INVENTORY_STOCK_CHECK_LIST_PAGE,
          component_value: ErrorTypeForAnalytics.UNKNOWN,
        });
      }
    },
    [
      context,
      validateStockCheck,
      sendSegmentTrackEvent,
      showTaskCompletedToast,
      navigateToOrigin,
      startNextCheck,
      navigateToCheck,
      showCheckAlreadyCompletedToast,
      showToastAndPlayError,
    ],
  );

  return {
    declareStock,
    isUpdating,
  };
}
